import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ColumnType, FilterConfig, FilterCondition, StringOperator, NumberOperator, BooleanOperator, DateOperator } from '@models/shared.models';

@Component({
  selector: 'app-generic-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule
  ],
  templateUrl: './generic-filter.component.html',
  styleUrl: './generic-filter.component.scss'
})
export class GenericFilterComponent {
  columns: { name: string; displayName: string; type: ColumnType }[] = [];
  filterConfig: FilterConfig = { conditions: [], operator: 'AND' };
  ColumnType = ColumnType;
  StringOperator = StringOperator;
  NumberOperator = NumberOperator;
  BooleanOperator = BooleanOperator;
  DateOperator = DateOperator;

  constructor(
    public dialogRef: MatDialogRef<GenericFilterComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { 
      columns: { name: string; displayName: string; type: ColumnType }[];
      activeFilter: FilterConfig | null;
    }
  ) {
    if (this.data?.columns) {
      this.columns = this.data.columns;
    }
    if (this.data?.activeFilter) {
      this.filterConfig = JSON.parse(JSON.stringify(this.data.activeFilter));
      
      // Convert date strings back to Date objects
      this.filterConfig.conditions.forEach((condition) => {
        if (condition.columnType === ColumnType.Date && condition.value) {
          condition.value = new Date(condition.value as unknown as string);
          if (condition.endValue) {
            condition.endValue = new Date(condition.endValue as unknown as string);
          }
        }
      });
    }
  }

  addCondition() {
    this.filterConfig.conditions.push({
      columnName: '',
      columnType: ColumnType.String,
      operator: StringOperator.Equals,
      value: ''
    });
  }

  removeCondition(conditionIndex: number) {
    this.filterConfig.conditions.splice(conditionIndex, 1);
  }

  getOperators(columnType: ColumnType): string[] {
    switch (columnType) {
      case ColumnType.String:
        return Object.values(StringOperator);
      case ColumnType.Number:
        return Object.values(NumberOperator);
      case ColumnType.Boolean:
        return Object.values(BooleanOperator);
      case ColumnType.Date:
        return Object.values(DateOperator);
      default:
        return Object.values(StringOperator);
    }
  }

  onColumnChange(condition: FilterCondition, columnName: string) {
    const selectedColumn = this.columns.find(col => col.name === columnName);
    if (selectedColumn) {
      condition.columnName = selectedColumn.name;
      condition.columnType = selectedColumn.type;
      
      switch (selectedColumn.type) {
        case ColumnType.String:
          condition.operator = StringOperator.Equals;
          condition.value = '';
          break;
        case ColumnType.Number:
          condition.operator = NumberOperator.Equals;
          condition.value = 0;
          break;
        case ColumnType.Boolean:
          condition.operator = BooleanOperator.Equals;
          condition.value = false;
          break;
        case ColumnType.Date:
          condition.operator = DateOperator.Equals;
          condition.value = new Date();
          condition.endValue = undefined;
          break;
        default:
          condition.operator = StringOperator.Equals;
          condition.value = '';
      }
    }
  }

  onOperatorChange(condition: FilterCondition) {
    if (condition.columnType === ColumnType.Boolean) {
      if (condition.operator === BooleanOperator.IsTrue) {
        condition.value = true;
      } else if (condition.operator === BooleanOperator.IsFalse) {
        condition.value = false;
      }
    }
    
    if (condition.columnType === ColumnType.Date) {
      if (condition.operator === DateOperator.Between) {
        condition.endValue = new Date();
      } else {
        condition.endValue = undefined;
      }
    }
  }

  hasActiveFilters(): boolean {
    return this.filterConfig.conditions.some((condition) => 
      condition.columnName && condition.value !== undefined
    );
  }

  applyFilter() {
    this.dialogRef.close(this.filterConfig);
  }

  clearFilter() {
    this.filterConfig = { conditions: [], operator: 'AND' };
  }

  formatOperator(operator: string): string {
    // Convert camelCase to Title Case with spaces
    const result = operator.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  }
  
  // Helper method to get display name for a column
  getColumnDisplayName(columnName: string): string {
    const column = this.columns.find(col => col.name === columnName);
    return column ? column.displayName : columnName;
  }
}
